/**
 * CommercialHeatPumpModelsAdmin — admin-only page for the Sun Stellar
 * Commercial Heat Pump Calculator's SKU catalogue (Calculator #2).
 *
 * Mirrors the structure of PoolModelsAdmin.tsx: TDS values (kW, COP, max
 * tank, compressor) are read-only physical product specs; the only field
 * this page mutates is the per-SKU stock status. Per SPECIFICATION.txt
 * §11.3 (inventory integration), stock status drives whether the engine
 * lists a SKU as "in stock", "low stock", or "made to order". Per §11.4,
 * pricing is NOT in scope for this page — it is applied at quote-generation
 * time, so the page shows a "Wired post-launch" placeholder.
 *
 * Per SPECIFICATION.txt §5, the catalogue contains 13 SKUs split across:
 *   - Domestic (4):           ST-HP-5X/300L, 7X/300L, 7X/500L, 11X/500L
 *   - Commercial Standard (4): ST-HP-20, 25, 40, 55
 *   - Commercial EVI (5):     ST-HP-EVI-11, 19, 38, 55, 110
 *
 * Catalog source: backend/src/modules/sun-stellar-commercial-heat-pump-calc/
 * catalog.ts. The page does NOT pull from a DB table — it pulls from the
 * backend's GET /v1/commercial-heat-pump/catalog/products endpoint (HW-B
 * owns this). Until that endpoint ships, the table renders the in-code
 * values and the stock toggle calls
 *   PATCH /v1/commercial-heat-pump/catalog/products/:model
 * If the endpoint is not yet wired, the toggle surfaces a friendly error
 * but the visual state still flips locally so HW-E QA can preview UX.
 *
 * Track D — Calculator #2 (HW-E, May 2026).
 *
 * TODO (parent integration): add `<Route path="/admin/commercial-heat-pump-models"
 *   element={<CommercialHeatPumpModelsAdmin />} />` to App.tsx (next to the
 *   existing `/admin/pool-models` route) once HW-A/B/C land.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2, AlertTriangle, Check, Info } from 'lucide-react';
import { api } from '@/utils/api';
import { useAuthStore } from '@/stores/auth';

type StockStatus = 'available' | 'low_stock' | 'made_to_order';

interface CatalogProductRow {
  /** SKU display code, e.g. "ST-HP-25" or "ST-HP-7X/500L" */
  model: string;
  /** Heating capacity (kW) */
  kw: number;
  /** Coefficient of Performance */
  cop: number;
  /** Compressor configuration */
  compressor: string;
  /** Tank size — for Domestic this is the integrated tank (L); for Commercial
   * this is maxTank (L) — the largest tank pairable to one machine. */
  tank_l: number;
  /** Recovery rate (L/hr) — only meaningful for Domestic. Optional. */
  recovery_lph?: number;
  /** Optional INR pricing — wired post-launch per §11.4. */
  price_inr?: number | null;
  /** Admin-controlled stock status. */
  stock_status: StockStatus;
}

interface CatalogResponse {
  domestic: CatalogProductRow[];
  commercial_std: CatalogProductRow[];
  commercial_evi: CatalogProductRow[];
}

// ─── In-code fallback (mirrors backend/src/modules/.../catalog.ts) ─────
// Used only if the GET /catalog/products endpoint is not yet wired by HW-B.
// Numbers MUST stay in sync with catalog.ts. If you change one here, change
// the other; otherwise the page lies about what the engine does.
const FALLBACK_CATALOG: CatalogResponse = {
  domestic: [
    { model: 'ST-HP-5X/300L',  kw: 5.0,  cop: 4.10, compressor: '1.5 HP Panasonic rotary', tank_l: 300, recovery_lph: 117, price_inr: null, stock_status: 'available' },
    { model: 'ST-HP-7X/300L',  kw: 7.0,  cop: 4.05, compressor: '1.5 HP Panasonic rotary', tank_l: 300, recovery_lph: 155, price_inr: null, stock_status: 'available' },
    { model: 'ST-HP-7X/500L',  kw: 7.0,  cop: 4.05, compressor: '2 HP Panasonic rotary',   tank_l: 500, recovery_lph: 155, price_inr: null, stock_status: 'available' },
    { model: 'ST-HP-11X/500L', kw: 11.0, cop: 4.10, compressor: '2 HP Panasonic rotary',   tank_l: 500, recovery_lph: 232, price_inr: null, stock_status: 'available' },
  ],
  commercial_std: [
    { model: 'ST-HP-20', kw: 20, cop: 4.25, compressor: '5 HP',       tank_l: 2000, price_inr: null, stock_status: 'available' },
    { model: 'ST-HP-25', kw: 25, cop: 4.23, compressor: '3 HP × 2',   tank_l: 2500, price_inr: null, stock_status: 'available' },
    { model: 'ST-HP-40', kw: 40, cop: 4.30, compressor: '5 HP × 2',   tank_l: 4000, price_inr: null, stock_status: 'available' },
    { model: 'ST-HP-55', kw: 55, cop: 4.30, compressor: '15 HP × 1',  tank_l: 5000, price_inr: null, stock_status: 'available' },
  ],
  commercial_evi: [
    { model: 'ST-HP-EVI-11',  kw: 8.9,  cop: 3.23, compressor: '1 × Copeland EVI', tank_l: 750,   price_inr: null, stock_status: 'available' },
    { model: 'ST-HP-EVI-19',  kw: 14.4, cop: 3.09, compressor: '1 × Copeland EVI', tank_l: 1500,  price_inr: null, stock_status: 'available' },
    { model: 'ST-HP-EVI-38',  kw: 30.2, cop: 3.36, compressor: '2 × Copeland EVI', tank_l: 3000,  price_inr: null, stock_status: 'available' },
    { model: 'ST-HP-EVI-55',  kw: 45,   cop: 3.62, compressor: '1 × Copeland EVI', tank_l: 5000,  price_inr: null, stock_status: 'available' },
    { model: 'ST-HP-EVI-110', kw: 88.5, cop: 3.61, compressor: '2 × Copeland EVI', tank_l: 10000, price_inr: null, stock_status: 'available' },
  ],
};

const STOCK_OPTIONS: { value: StockStatus; label: string; bg: string; fg: string }[] = [
  { value: 'available',     label: 'IN STOCK',      bg: '#d8f5d8', fg: '#065c0c' },
  { value: 'low_stock',     label: 'LOW STOCK',     bg: '#fff3cd', fg: '#7a5800' },
  { value: 'made_to_order', label: 'MADE TO ORDER', bg: '#fde7d8', fg: '#8a3a08' },
];

type TabKey = 'domestic' | 'commercial_std' | 'commercial_evi';

const TAB_LABELS: Record<TabKey, { title: string; subtitle: string }> = {
  domestic: {
    title: 'Domestic',
    subtitle: '4 SKUs · integrated tank · loads under 15 kW',
  },
  commercial_std: {
    title: 'Commercial Standard',
    subtitle: '4 SKUs · ST-HP series · separate tanks · −7°C to +45°C',
  },
  commercial_evi: {
    title: 'Commercial EVI',
    subtitle: '5 SKUs · sub-zero · Copeland EVI · down to −30°C',
  },
};

export function CommercialHeatPumpModelsAdmin() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = (user?.role?.name ?? '').toLowerCase() === 'admin';

  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [savingModel, setSavingModel] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('domestic');

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    api
      .get<CatalogResponse>('/commercial-heat-pump/catalog/products')
      .then((r) => setCatalog(r.data))
      .catch((e) => {
        // If the endpoint isn't wired yet (404 or network), fall back to the
        // in-code catalogue so the page is still useful for QA. The toggle
        // will show a friendly error when actually clicked.
        const status = e?.response?.status;
        if (status === 404 || status === undefined) {
          setCatalog(FALLBACK_CATALOG);
          setWarning(
            'Backend catalog endpoint not yet wired. Showing in-code values. ' +
              'Stock toggle will fail until HW-B ships GET/PATCH ' +
              '/commercial-heat-pump/catalog/products.'
          );
        } else {
          setError(e?.response?.data?.error ?? 'Could not load catalog.');
        }
      })
      .finally(() => setLoading(false));
  }, [isAdmin]);

  async function setStock(model: string, next: StockStatus) {
    if (!catalog) return;
    setSavingModel(model);
    setError(null);
    try {
      const res = await api.patch<CatalogProductRow>(
        `/commercial-heat-pump/catalog/products/${encodeURIComponent(model)}`,
        { stock_status: next }
      );
      // Optimistic-then-merge: replace just this row across all 3 lists.
      const updated: CatalogProductRow = res.data;
      setCatalog((prev) => prev && {
        domestic:        prev.domestic.map((r) => (r.model === model ? updated : r)),
        commercial_std:  prev.commercial_std.map((r) => (r.model === model ? updated : r)),
        commercial_evi:  prev.commercial_evi.map((r) => (r.model === model ? updated : r)),
      });
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 404 || status === undefined) {
        // Endpoint not yet implemented. Flip locally so QA can preview UX.
        setCatalog((prev) => prev && {
          domestic:        prev.domestic.map((r) => (r.model === model ? { ...r, stock_status: next } : r)),
          commercial_std:  prev.commercial_std.map((r) => (r.model === model ? { ...r, stock_status: next } : r)),
          commercial_evi:  prev.commercial_evi.map((r) => (r.model === model ? { ...r, stock_status: next } : r)),
        });
        setError(
          `PATCH /commercial-heat-pump/catalog/products/${model} not yet wired. ` +
            'Local state updated for preview only — refresh will revert.'
        );
      } else {
        setError(e?.response?.data?.error ?? 'Could not update stock status.');
      }
    } finally {
      setSavingModel(null);
    }
  }

  const activeRows: CatalogProductRow[] = useMemo(() => {
    if (!catalog) return [];
    return catalog[tab];
  }, [catalog, tab]);

  if (!isAdmin) {
    return (
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '40px 20px' }}>
        <h1 style={{ fontFamily: '"Fraunces", serif', fontSize: 28, fontWeight: 600, color: '#003a6b', margin: 0 }}>
          Commercial Heat Pump Catalog
        </h1>
        <div style={emptyStateBox}>Admin only.</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: '24px 20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontFamily: '"Fraunces", serif', fontSize: 28, fontWeight: 600, color: '#003a6b', margin: 0 }}>
          Commercial Heat Pump Catalog
        </h1>
        <span style={catalogSourcePill}>
          <Info size={12} /> Catalog source: in-code (catalog.ts)
        </span>
      </div>

      <p style={{ color: '#666', marginTop: 4, lineHeight: 1.5 }}>
        Domestic, Commercial ST-HP and Commercial EVI catalogues for the Sun Stellar
        Commercial Heat Pump (Hot Water) Sizing Calculator. TDS values (kW, COP, max
        tank, compressor) are read-only and shipped from Sun Stellar Engineering.
        The only editable field is stock status — flip it as inventory changes; the
        engine pushes out-of-stock SKUs into the alternative-recommendation slot.
        Pricing is wired post-launch (per §11.4 — applied at quote generation).
      </p>

      {warning && (
        <div style={{ ...alertBox, background: '#fff8e1', color: '#7a5800' }}>
          <AlertTriangle size={16} /> {warning}
        </div>
      )}

      {error && (
        <div style={{ ...alertBox, background: '#fde7e7', color: '#8b2222' }}>
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {/* Tabs */}
      <div role="tablist" style={tabRow}>
        {(Object.keys(TAB_LABELS) as TabKey[]).map((key) => {
          const meta = TAB_LABELS[key];
          const count = catalog?.[key]?.length ?? 0;
          const active = tab === key;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(key)}
              style={{
                ...tabButton,
                background: active ? '#003a6b' : '#f3f8ff',
                color: active ? '#fff' : '#003a6b',
                borderBottomColor: active ? '#003a6b' : 'transparent',
              }}
            >
              <span style={{ fontWeight: 600 }}>{meta.title}</span>
              <span style={{ fontSize: 11, opacity: 0.85 }}>({count})</span>
            </button>
          );
        })}
      </div>

      <p style={{ color: '#777', fontSize: 12, marginTop: 4, marginBottom: 12 }}>
        {TAB_LABELS[tab].subtitle}
      </p>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <Loader2 className="animate-spin" />
        </div>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr style={{ background: '#f3f8ff', textAlign: 'left' }}>
              <th style={th}>Model</th>
              <th style={th}>kW</th>
              <th style={th}>{tab === 'domestic' ? 'Tank (L)' : 'Max tank (L)'}</th>
              <th style={th}>COP</th>
              <th style={th}>Compressor</th>
              {tab === 'domestic' && <th style={th}>Recovery (L/hr)</th>}
              <th style={th}>Price (INR)</th>
              <th style={th}>Stock status</th>
            </tr>
          </thead>
          <tbody>
            {activeRows.map((r) => {
              const isSaving = savingModel === r.model;
              const opt = STOCK_OPTIONS.find((s) => s.value === r.stock_status) ?? STOCK_OPTIONS[0]!;
              return (
                <tr key={r.model} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={td}><strong>{r.model}</strong></td>
                  <td style={td}>{r.kw}</td>
                  <td style={td}>{r.tank_l.toLocaleString('en-IN')}</td>
                  <td style={td}>{r.cop.toFixed(2)}</td>
                  <td style={td}>{r.compressor}</td>
                  {tab === 'domestic' && <td style={td}>{r.recovery_lph ?? '—'}</td>}
                  <td style={{ ...td, color: '#999', fontStyle: 'italic', fontSize: 12 }}>
                    Wired post-launch
                  </td>
                  <td style={td}>
                    <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                      <select
                        value={r.stock_status}
                        onChange={(e) => setStock(r.model, e.target.value as StockStatus)}
                        disabled={isSaving}
                        aria-label={`Stock status for ${r.model}`}
                        style={{
                          padding: '4px 8px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 600,
                          border: 0,
                          cursor: isSaving ? 'wait' : 'pointer',
                          background: opt.bg,
                          color: opt.fg,
                        }}
                      >
                        {STOCK_OPTIONS.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                      {isSaving ? (
                        <Loader2 size={12} className="animate-spin" color="#777" />
                      ) : (
                        <Check size={12} color="#999" />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────
const th: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '2px solid #ddd',
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 13 };
const tableStyle: React.CSSProperties = {
  width: '100%',
  marginTop: 8,
  borderCollapse: 'collapse',
};
const tabRow: React.CSSProperties = {
  display: 'flex',
  gap: 4,
  marginTop: 16,
  borderBottom: '1px solid #ddd',
};
const tabButton: React.CSSProperties = {
  padding: '8px 16px',
  border: 0,
  borderBottom: '3px solid transparent',
  borderRadius: '6px 6px 0 0',
  cursor: 'pointer',
  fontSize: 13,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  transition: 'background 0.15s',
};
const alertBox: React.CSSProperties = {
  padding: 12,
  borderRadius: 6,
  marginTop: 12,
  display: 'flex',
  gap: 8,
  alignItems: 'flex-start',
  fontSize: 13,
  lineHeight: 1.4,
};
const catalogSourcePill: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: '#eef4fb',
  color: '#003a6b',
  fontSize: 11,
  fontWeight: 500,
  padding: '4px 10px',
  borderRadius: 999,
};
const emptyStateBox: React.CSSProperties = {
  marginTop: 32,
  padding: 32,
  background: '#f7f7f7',
  border: '1px dashed #ccc',
  borderRadius: 8,
  textAlign: 'center',
  color: '#666',
};
